import React, { useState, useEffect } from 'react';
import { GovernanceColors } from '@/config/governanceColors';
import { Sidebar } from './Sidebar';
import { OfflineBanner } from './OfflineBanner';
import { DemoOverlay } from './DemoOverlay';
import { FleetView } from './FleetView';
import { AuditView } from './AuditView';
import { SteerView } from './SteerView';
import { DiscoveryView } from './DiscoveryView';
import { ComplianceView } from './ComplianceView';
import { SecurityView } from './SecurityView';
import { GDPRView } from './GDPRView';
import { ExplainabilityView } from './ExplainabilityView';
import { DesktopDaemonView } from './DesktopDaemonView';
import { TrustAnomalyView } from './TrustAnomalyView';
import { IPIDefenseView } from './IPIDefenseView';
import { WorkflowGovernanceView } from './WorkflowGovernanceView';
import { ThreatIntelligenceView } from './ThreatIntelligenceView';
import ChatUI from '../ChatUI';
import OSINTThreatDetectionPanel from '../OSINTThreatDetectionPanel';
import { HaltModal } from './HaltModal';
import { KeyboardHelp } from './KeyboardHelp';
import VoiceControl from './VoiceControl';
import { useOnlineStatus, useDemoMode } from '@/hooks/useCommandCenter';
import { Shield, Play, Square, Wifi, WifiOff, AlertTriangle } from 'lucide-react';

class ViewErrorBoundary extends React.Component<{ children: React.ReactNode; viewName: string }, { hasError: boolean; error: string }> {
  constructor(props: any) { super(props); this.state = { hasError: false, error: '' }; }
  static getDerivedStateFromError(error: Error) { return { hasError: true, error: error.message }; }
  override render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-64 gap-4 text-[#A8A29E]">
          <AlertTriangle className="w-10 h-10 text-amber-400" />
          <p className="text-sm">Failed to load {this.props.viewName}</p>
          <p className="text-xs text-[#8F857B]">{this.state.error}</p>
          <button onClick={() => this.setState({ hasError: false, error: '' })} className="text-xs hover:underline" style={{ color: GovernanceColors.primary }}>Retry</button>
        </div>
      );
    }
    return this.props.children;
  }
}

export type ViewId = 'fleet' | 'audit' | 'steer' | 'discovery' | 'compliance' | 'security' | 'gdpr' | 'explainability' | 'desktop' | 'chat' | 'threats' | 'trust-anomaly' | 'ipi-defense' | 'workflow-governance' | 'threat-intel';

export default function CommandCenter() {
  const [currentView, setCurrentView] = useState<ViewId>('fleet');
  const [haltModalOpen, setHaltModalOpen] = useState(false);
  const [keyboardHelpOpen, setKeyboardHelpOpen] = useState(false);
  const online = useOnlineStatus();
  const demo = useDemoMode();

  // Global keyboard shortcuts. Documented in <KeyboardHelp/>; tested in
  // test/command-center.test.tsx. Wired here so every view in CommandCenter
  // shares the same shortcuts without each view having to subscribe.
  // - Ctrl+Shift+K   : open emergency halt modal
  // - ?              : open keyboard help overlay
  // - Escape         : close whichever modal is open
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Skip when typing in an input/textarea so '?' isn't hijacked from text fields.
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      const inEditable = tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable;
      if (e.key === 'Escape') {
        if (keyboardHelpOpen) {setKeyboardHelpOpen(false);}
        if (haltModalOpen) {setHaltModalOpen(false);}
        return;
      }
      if (inEditable) {return;}
      if (e.ctrlKey && e.shiftKey && (e.key === 'K' || e.key === 'k')) {
        e.preventDefault();
        setHaltModalOpen(true);
        return;
      }
      if (e.key === '?') {
        e.preventDefault();
        setKeyboardHelpOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [haltModalOpen, keyboardHelpOpen]);

  const handleDemoToggle = () => {
    if (demo.running) {
      demo.stop();
    } else {
      demo.start(setCurrentView as (view: string) => void);
    }
  };

  const viewComponents: Record<ViewId, () => React.ReactNode> = {
    fleet: () => <FleetView demoActive={demo.running} demoStep={demo.step} demoStepKind={demo.evidence?.slice(-1)[0]?.kind} />,
    audit: () => <AuditView />,
    steer: () => <SteerView />,
    discovery: () => <DiscoveryView />,
    compliance: () => <ComplianceView />,
    security: () => <SecurityView />,
    gdpr: () => <GDPRView />,
    explainability: () => <ExplainabilityView />,
    desktop: () => <DesktopDaemonView />,
    chat: () => <ChatUI isDemoMode={demo.running} />,
    threats: () => <OSINTThreatDetectionPanel />,
    'trust-anomaly': () => <TrustAnomalyView />,
    'ipi-defense': () => <IPIDefenseView />,
    'workflow-governance': () => <WorkflowGovernanceView />,
    'threat-intel': () => <ThreatIntelligenceView />,
  };

  return (
    <div className="min-h-screen bg-[#0B0A09] text-[#F5F0E8]">
      {!online && <OfflineBanner />}
      {demo.running && <DemoOverlay message={demo.steps[demo.step]?.message || ''} evidence={demo.evidence} />}

      <Sidebar currentView={currentView} onViewChange={setCurrentView} />

      <div className="ml-[220px] flex flex-col min-h-screen">
        {/* Header. Extra right padding (pr-28) reserves space for the app-level
            fixed "Sign out" button (ui/app.tsx, position:fixed top-right) so it
            no longer overlaps the "Run Demo" control at the end of this row. */}
        <header className="flex items-center justify-between pl-6 pr-28 py-4 border-b border-[#2E2A26]">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6" style={{ color: GovernanceColors.primary }} />
            <h1 className="text-xl font-semibold">Command Center</h1>
          </div>
          <div className="flex items-center gap-4">
            {/* Theme toggle removed until proper var(--cc-*) CSS-variable adoption ships;
                the Sun/Moon button was misleading (clicked but had no visual effect because
                CommandCenter components use hardcoded hex colors, not the themed CSS vars
                that ThemeToggle injects). Restore when components consume var(--cc-*). */}
            {/* Online status */}
            <div className="flex items-center gap-2">
              {online ? (
                <>
                  <Wifi className="w-4 h-4" style={{ color: GovernanceColors.healthy }} />
                  <span className="text-xs" style={{ color: GovernanceColors.healthy }}>Online</span>
                </>
              ) : (
                <>
                  <WifiOff className="w-4 h-4" style={{ color: GovernanceColors.critical }} />
                  <span className="text-xs" style={{ color: GovernanceColors.critical }}>Offline</span>
                </>
              )}
            </div>
            {/* Demo button */}
            <button
              onClick={handleDemoToggle}
              className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm transition-colors ${
                demo.running
                  ? 'bg-amber-600 hover:bg-amber-700 text-white'
                  : 'bg-[#292524] hover:bg-[#3D3833] text-[#A8A29E] hover:text-[#F5F0E8] border border-[#3A352F]'
              }`}
            >
              {demo.running ? (
                <><Square className="w-3 h-3" /> Stop Demo</>
              ) : (
                <><Play className="w-3 h-3" /> Run Demo</>
              )}
            </button>
          </div>
        </header>

        {/* Main content */}
        <main className="flex-1 p-6">
          <ViewErrorBoundary viewName={currentView} key={currentView}>
            {viewComponents[currentView]()}
          </ViewErrorBoundary>
        </main>
      </div>

      {/* Governed voice actions -- read-only navigation + draft-only chat,
          classified server-side (/api/voice/classify), executed client-side
          only behind the confirm card. Ctrl+Shift+V summons (start-only). */}
      <VoiceControl currentView={currentView} onNavigate={setCurrentView} />

      {/* Global modals -- toggled by keyboard shortcuts (Ctrl+Shift+K / ?) */}
      <HaltModal
        open={haltModalOpen}
        onClose={() => setHaltModalOpen(false)}
        onComplete={() => setHaltModalOpen(false)}
      />
      <KeyboardHelp
        open={keyboardHelpOpen}
        onClose={() => setKeyboardHelpOpen(false)}
      />
    </div>
  );
}
